import React, { useState, useEffect } from 'react';
import { holidayApi, employeeApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { canManageHolidays } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { Plus, Edit, CalendarDays, Trash2, Cake, User } from 'lucide-react';

const birthdayDisplayName = (emp) => {
  const full = `${emp?.firstName ?? ''} ${emp?.lastName ?? ''}`.trim();
  return full || emp?.empId || 'Employee';
};

const formatBirthdayDate = (raw) => {
  if (!raw) return '—';
  const iso = String(raw).slice(0, 10);
  const parts = iso.split('-');
  if (parts.length === 3) {
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (month && day) {
      return new Date(2000, month - 1, day).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
      });
    }
  }
  return String(raw);
};

const BIRTHDAY_GROUPS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'dayAfterTomorrow', label: 'Day after tomorrow' },
];

const emptyBirthdayGroups = () => ({
  today: [],
  tomorrow: [],
  dayAfterTomorrow: [],
});

export default function ShiftHolidayPage() {
  const { user } = useAuth();
  const canEdit = canManageHolidays(user?.role);
  const [holidays, setHolidays] = useState([]);
  const [birthdays, setBirthdays] = useState(emptyBirthdayGroups);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('holidays');
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [editHoliday, setEditHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ name: '', date: '', type: 'National' });
  const [deleteHoliday, setDeleteHoliday] = useState(null);

  const normalizeArrayResponse = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.employees)) return value.employees;
    return [];
  };

  const normalizeObjectResponse = (value) => {
    if (value?.data && typeof value.data === 'object' && !Array.isArray(value.data)) return value.data;
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    return {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [holsData, birthdaysData] = await Promise.all([
          holidayApi.getAll().catch(() => []),
          employeeApi.getBirthdays().catch(() => emptyBirthdayGroups()),
        ]);
        setHolidays(normalizeArrayResponse(holsData));
        setBirthdays(birthdaysData ?? emptyBirthdayGroups());
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Holiday dates for calendar highlighting
  const holidayDates = holidays.map(h => new Date(h.date));

  const handleSaveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.date) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      if (editHoliday) {
        const updated = normalizeObjectResponse(await holidayApi.update(editHoliday.holId || editHoliday.id, holidayForm));
        setHolidays(prev => prev.map(h => (h.holId || h.id) === (editHoliday.holId || editHoliday.id) ? { ...h, ...updated } : h));
        toast.success('Holiday updated');
      } else {
        const newHol = normalizeObjectResponse(await holidayApi.create(holidayForm));
        setHolidays(prev => [...prev, newHol]);
        toast.success('Holiday added');
      }
      setHolidayOpen(false);
      setEditHoliday(null);
      setHolidayForm({ name: '', date: '', type: 'National' });
    } catch (err) {
      toast.error(err.message || 'Failed to save holiday');
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await holidayApi.remove(id);
      setHolidays(prev => prev.filter(h => (h.holId || h.id) !== id));
      toast.success('Holiday removed');
    } catch (err) {
      toast.error(err.message || 'Failed to delete holiday');
    }
  };

  const handleConfirmDeleteHoliday = async () => {
    if (!deleteHoliday?.id) return;
    const id = deleteHoliday.id;
    setDeleteHoliday(null);
    await handleDeleteHoliday(id);
  };

  const typeColor = (type) => {
    if (type === 'National') return 'bg-blue-100 text-blue-700 border-blue-200';
    if (type === 'Festival') return 'bg-purple-100 text-purple-700 border-purple-200';
    return 'bg-orange-100 text-orange-700 border-orange-200';
  };

  const birthdayCount =
    (birthdays?.today?.length ?? 0) +
    (birthdays?.tomorrow?.length ?? 0) +
    (birthdays?.dayAfterTomorrow?.length ?? 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="shift-holiday-page" className="space-y-3 animate-fadeIn">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">Holidays & Birthdays</h1>
        <p className="text-muted-foreground mt-1">Manage company holidays and birthdays</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="holidays" data-testid="tab-holidays">
            <CalendarDays className="w-4 h-4 mr-1" /> Holidays
          </TabsTrigger>
          {/* <TabsTrigger value="shifts" data-testid="tab-shifts">
            <Clock className="w-4 h-4 mr-1" /> Shift Roster
          </TabsTrigger> */}
        </TabsList>

        <TabsContent value="holidays">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Holiday Calendar */}
            <Card className="lg:col-span-5 border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-base font-[Manrope]">Holiday Calendar</CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center">
                <Calendar
                  mode="multiple"
                  selected={holidayDates}
                  className="rounded-md"
                  modifiers={{ holiday: holidayDates }}
                  modifiersStyles={{ holiday: { backgroundColor: '#0047AB20', color: '#0047AB', fontWeight: 'bold' } }}
                />
              </CardContent>
            </Card>

            {/* Holiday List */}
            <Card className="lg:col-span-7 border border-border shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-[Manrope]">Holiday List 2026</CardTitle>
                {canEdit && (
                <Button
                  size="sm"
                  onClick={() => { setEditHoliday(null); setHolidayForm({ name: '', date: '', type: 'National' }); setHolidayOpen(true); }}
                  className="bg-[#0047AB] hover:bg-[#003d96] text-white"
                  data-testid="add-holiday-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Holiday
                </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Day</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
                      {canEdit && <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holidays.sort((a, b) => new Date(a.date) - new Date(b.date)).map(h => (
                      <TableRow key={h.holId || h.id} data-testid={`holiday-row-${h.holId || h.id}`}>
                        <TableCell className="text-sm font-medium">{h.name}</TableCell>
                        <TableCell className="font-mono text-xs">{h.date}</TableCell>
                        <TableCell className="text-xs">{new Date(h.date).toLocaleDateString('en', { weekday: 'long' })}</TableCell>
                        <TableCell><Badge className={typeColor(h.type)}>{h.type}</Badge></TableCell>
                        {canEdit && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => { setEditHoliday(h); setHolidayForm({ name: h.name, date: h.date, type: h.type }); setHolidayOpen(true); }}
                              data-testid={`edit-holiday-${h.holId || h.id}`}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              onClick={() => setDeleteHoliday({ id: h.holId || h.id, name: h.name })}
                              data-testid={`delete-holiday-${h.holId || h.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {holidays.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canEdit ? 5 : 4} className="text-center py-8 text-muted-foreground">
                          <CalendarDays className="w-6 h-6 mx-auto mb-2 opacity-50" />
                          No holidays configured
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-border shadow-sm mt-6" data-testid="birthday-card">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                <Cake className="w-4 h-4 text-[#0047AB] shrink-0" />
                Birthdays
              </CardTitle>
              <Badge variant="outline" className="font-mono">{birthdayCount}</Badge>
            </CardHeader>
            <CardContent>
              {birthdayCount > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {BIRTHDAY_GROUPS.map(({ key, label }) => {
                    const people = birthdays?.[key] ?? [];
                    return (
                      <div key={key} className="rounded-lg border border-border min-w-0" data-testid={`birthday-group-${key}`}>
                        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/40">
                          <p className="text-xs uppercase tracking-widest font-medium text-muted-foreground">{label}</p>
                          <Badge variant="outline" className="font-mono text-[10px]">{people.length}</Badge>
                        </div>
                        {people.length > 0 ? (
                          <ul className="divide-y divide-border">
                            {people.map((emp, index) => {
                              const keyId = emp?.empId || `${key}-${index}`;
                              const name = birthdayDisplayName(emp);
                              return (
                                <li key={keyId} className="flex items-center gap-3 px-3 py-3" data-testid={`birthday-row-${keyId}`}>
                                  {emp?.picture ? (
                                    <img src={emp.picture} alt={name} className="w-9 h-9 rounded-full object-cover border border-border shrink-0" />
                                  ) : (
                                    <div className="w-9 h-9 rounded-full bg-[#0047AB]/10 flex items-center justify-center shrink-0">
                                      <User className="w-4 h-4 text-[#0047AB]/50" />
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">{name}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {[emp?.designation, emp?.department].filter(Boolean).join(' · ') || emp?.empId}
                                    </p>
                                  </div>
                                  <p className="text-xs font-mono text-[#0047AB] shrink-0">{formatBirthdayDate(emp?.dob)}</p>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground px-3 py-6 text-center">None</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Cake className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No birthdays today, tomorrow, or the day after</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* <TabsContent value="shifts">
          <Card className="border border-border shadow-sm">
            <CardHeader>
              <CardTitle className="text-base font-[Manrope]">Employee Shift Roster</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Shift Timing</TableHead>
                    {DAYS.map(d => (
                      <TableHead key={d} className="text-xs uppercase tracking-widest text-center">{d.slice(0, 3)}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shifts.map(shift => {
                    const emp = employees.find(e => e.empId === shift.employeeId || e.id === shift.employeeId);
                    return (
                      <TableRow key={shift.shiftId || shift.id} data-testid={`shift-row-${shift.shiftId || shift.id}`}>
                        <TableCell className="text-sm font-medium">{shift.employeeName}</TableCell>
                        <TableCell className="font-mono text-xs">{shift.shiftTiming}</TableCell>
                        {DAYS.map(day => {
                          const isOff = (shift.offDays || []).includes(day);
                          return (
                            <TableCell key={day} className="text-center">
                              <button
                                className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                                  isOff ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700 border border-green-200'
                                }`}
                                onClick={() => {
                                  const newOffDays = isOff
                                    ? (shift.offDays || []).filter(d => d !== day)
                                    : [...(shift.offDays || []), day];
                                  handleShiftUpdate(shift.shiftId || shift.id, newOffDays);
                                }}
                                data-testid={`shift-toggle-${shift.shiftId || shift.id}-${day}`}
                              >
                                {isOff ? 'OFF' : 'ON'}
                              </button>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {shifts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
                        No shift data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent> */}
      </Tabs>

      {/* Holiday Dialog */}
      {canEdit && (
      <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{editHoliday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
            <DialogDescription>Fill in the holiday details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Holiday Name</Label>
              <Input value={holidayForm.name} onChange={e => setHolidayForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Diwali" data-testid="holiday-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date</Label>
              <Input type="date" value={holidayForm.date} onChange={e => setHolidayForm(p => ({ ...p, date: e.target.value }))} data-testid="holiday-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label>
              <Select value={holidayForm.type} onValueChange={v => setHolidayForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="National">National</SelectItem>
                  <SelectItem value="Festival">Festival</SelectItem>
                  <SelectItem value="Religious">Religious</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveHoliday} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="save-holiday-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      <ConfirmDialog
        open={!!deleteHoliday}
        onOpenChange={(open) => !open && setDeleteHoliday(null)}
        title="Delete Holiday?"
        description={`Are you sure you want to delete "${deleteHoliday?.name || 'this holiday'}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDeleteHoliday}
        data-testid="holiday-delete-confirm"
      />
    </div>
  );
}
